import { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-premium';

type ValueOption = { label: string; value: any };
type AccessibleColumn = {
  attrInternalName: string;
  attrDataType: string;
  valueOptions?: ValueOption[];
};
type ExtendedGridFilterItem = GridFilterItem & {
  meta?: {
    includeNullForDoesNotContain?: boolean;
    useNilikeForDoesNotContain?: boolean;
  };
};

type StringOptionCheck = {
  hasStringOptions: boolean;
  nonStringOptions: ValueOption[];
  totalOptions: number;
  meaningfulOptionsCount: number;
};

/**
 * Checks whether a singleSelect column's options are all string label/value pairs.
 * Ignores null/undefined label/value entries.
 */
function evaluateStringOptions(options: ValueOption[] = []): StringOptionCheck {
  const meaningfulOptions = options.filter(
    (opt) =>
      opt?.label !== null &&
      opt?.label !== undefined &&
      opt?.value !== null &&
      opt?.value !== undefined,
  );

  const hasStringOptions =
    meaningfulOptions.length > 0 &&
    meaningfulOptions.every(
      (opt) => typeof opt.label === 'string' && typeof opt.value === 'string',
    );

  const nonStringOptions = !hasStringOptions
    ? meaningfulOptions
        .filter((opt) => typeof opt.label !== 'string' || typeof opt.value !== 'string')
        .slice(0, 5)
    : [];

  return {
    hasStringOptions,
    nonStringOptions,
    totalOptions: options.length,
    meaningfulOptionsCount: meaningfulOptions.length,
  };
}

/**
 * Determines whether _nilike can be safely used for doesNotContain on singleSelect.
 * When dropdown is of string like mpno field
 * Logs the decision details for diagnostics.
 */
function getNilikeDecision(
  item: GridFilterItem,
  options: ValueOption[],
  matchedValues: any[],
): boolean {
  const { hasStringOptions } = evaluateStringOptions(options);
  const hasStringValues =
    matchedValues.length > 0 && matchedValues.every((v) => typeof v === 'string');
  const hasStringInput = typeof item.value === 'string';
  const useNilikeForDoesNotContain =
    item.operator === 'doesNotContain' && hasStringOptions && hasStringValues && hasStringInput;

  return useNilikeForDoesNotContain;
}

// Normalizes a value to a searchable string.
function asSearchToken(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

// Builds a RegExp matcher function for wildcards (*, %) and escapes special characters.
function buildMatcher(raw: unknown): (candidate: unknown) => boolean {
  const token = asSearchToken(raw);
  if (!token) return () => false;

  // Allow * or % as wildcards
  const escaped = token
    .replace(/[%*]/g, '__WILDCARD__')
    .replace(/[-/\\^$+?.()|[\]{}]/g, '\\$&')
    .replace(/__WILDCARD__/g, '.*');

  const regex = new RegExp(escaped, 'i');

  return (candidate: unknown) => regex.test(asSearchToken(candidate));
}

// --- Helper Functions for Transformation ---

// Checks if a GridFilterItem is a single-select filter eligible for transformation.
function canTransformItem(
  item: GridFilterItem,
  allAccessibleColumns: AccessibleColumn[],
): AccessibleColumn | null {
  if (
    !item ||
    !item.field ||
    Array.isArray(item.value) ||
    !['contains', 'doesNotContain'].includes(item.operator)
  ) {
    return null;
  }

  // Find the corresponding singleSelect column
  const column = allAccessibleColumns.find(
    (c) => c.attrInternalName === item.field && c.attrDataType === 'singleSelect',
  );

  if (column && Array.isArray(column.valueOptions)) {
    return column;
  }

  return null;
}

//  Performs the wildcard/label-based search against the column's value options.
function getMatchedValues(itemValue: unknown, column: AccessibleColumn): any[] {
  const matcher = buildMatcher(itemValue);
  const options = column.valueOptions || [];

  const matchedValues = options.filter((opt) => matcher(opt.label)).map((opt) => opt.value);

  return matchedValues;
}

/**
 * Applies the logic to transform a single-select filter item's operator and value
 * based on the matched values and original operator.
 */
function transformSingleSelectItem(
  item: GridFilterItem,
  column: AccessibleColumn,
  matchedValues: any[],
): ExtendedGridFilterItem {
  const inferNoMatchValue = () => {
    if (!column.valueOptions || column.valueOptions.length === 0) {
      return -1;
    }

    const sample = (column.valueOptions || []).find(
      (opt) => opt?.value !== null && opt?.value !== undefined,
    )?.value;

    if (typeof sample === 'number') {
      return -1;
    }

    return item.value;
  };

  const rawInput = item.value;
  const isEmptyInput =
    rawInput === null ||
    rawInput === undefined ||
    (typeof rawInput === 'string' && rawInput.trim() === '');

  let safeValues: any[] | undefined;

  const isDoesNotContain = item.operator === 'doesNotContain';
  const options = column.valueOptions || [];
  const useNilikeForDoesNotContain = getNilikeDecision(item, options, matchedValues);

  if (isDoesNotContain) {
    if (isEmptyInput) {
      safeValues = undefined; // No filter when no value
    } else {
      safeValues = matchedValues; // Use _nin with matched values
    }
  } else {
    if (matchedValues.length > 0) {
      safeValues = matchedValues;
    } else if (isEmptyInput) {
      safeValues = undefined;
    } else {
      safeValues = [inferNoMatchValue()];
    }
  }

  return {
    ...item,
    operator: isDoesNotContain ? 'notIn' : 'isAnyOf',
    value: safeValues,
    meta: isDoesNotContain
      ? { includeNullForDoesNotContain: true, useNilikeForDoesNotContain }
      : undefined,
  };
}

/**
 * Transforms filter items that perform string-based search on singleSelect columns
 * into 'isAnyOf' operators with an array of matching option values.
 * This function is now the orchestrator.
 *
 * @param filterModel GridFilterModel Incoming filter model from the DataGrid.
 * @param allAccessibleColumns AccessibleColumn[] | null | undefined List of columns (only singleSelect columns are transformed).
 * @returns GridFilterModel A possibly transformed copy of the filter model .
 */
export function transformFilterItems(
  filterModel: GridFilterModel,
  allAccessibleColumns: AccessibleColumn[] | null | undefined,
): GridFilterModel {
  if (!filterModel?.items?.length || !allAccessibleColumns?.length) {
    return filterModel;
  }

  const newItems = filterModel.items.map((item) => {
    const column = canTransformItem(item, allAccessibleColumns);
    if (!column) {
      return item;
    }

    const matchedValues = getMatchedValues(item.value, column);

    const newItem = transformSingleSelectItem(item, column, matchedValues);

    return newItem;
  });

  return { ...filterModel, items: newItems };
}
